import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import {
  NavigateNext,
  Close,
  FileUpload,
  TextFields,
  CheckBox,
} from "@mui/icons-material";

import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import useDebounce from "../../../hooks/useDebounce";
import { RootState } from "../../../store/Index";
import { StreamsIcon } from "../../../components/icons";

type TaskItem = {
  name: string;
  status: "pending" | "completed";
};

type DraftTask = {
  title: string;
  items: TaskItem[];
  draftItem: string;
};





export interface RoleItem {
  id: number;
  name: string;
}

interface SearchRoleDialogProps {
  searchRoleOpen: boolean;
  setSearchRoleOpen: (open: boolean) => void;

  searchResults: RoleItem | null;
  setSearchResults: (role: RoleItem | null) => void;

  setAddRoleOpen: (open: boolean) => void;
  setAddTaskOpen: (open: boolean) => void;

  onAddChecklist: (data: {
    role: RoleItem | null;
    checklistItem: string;
    usage: string | null;
    description: string;
    tasks: {
      title: string;
      task_item: {
        name: string;
        status: "pending" | "completed";
      }[];
    }[];
  }) => void;
}

const inputTypeOptions = ["Text Input", "File upload", "Checkbox"];
const usageOptions = [
  "Pre Shoot",
  "Post Shoot",

  "Post Production",
  "Final Cut",
];

export const SearchRoleDialog: React.FC<SearchRoleDialogProps> = ({
  searchRoleOpen,
  setSearchRoleOpen,
  searchResults,
  setSearchResults,
  setAddRoleOpen,
  setAddTaskOpen,
  onAddChecklist,
}) => {
  const [roleOptions, setRoleOptions] = useState<RoleItem[]>([]);
  const [roleVal, setRoleVal] = useState("");
  const [rolesLoading, setRolesLoading] = useState(false);
  const [checklistItem, setChecklistItem] = useState<string>("");
 
  const [selectedUsage, setSelectedUsage] = useState<string | null>(null);
  const debouncedRoleSearch = useDebounce(roleVal, 400);
  const reduxTheme = useSelector((state: RootState) => state.theme);
 const [draftTask, setDraftTask] = useState<DraftTask>({
   title: "",
   items: [],
   draftItem: "",
 });

 const [savedTasks, setSavedTasks] = useState<
   { title: string; task_item: TaskItem[] }[]
 >([]);

 const addDraftItem = () => {
   if (!draftTask.draftItem.trim()) return;

   setDraftTask((prev) => ({
     ...prev,
     items: [...prev.items, { name: prev.draftItem, status: "pending" }],
     draftItem: "",
   }));
 };
const commitTask = () => {
  if (!draftTask.title.trim() || draftTask.items.length === 0) return;

  setSavedTasks((prev) => [
    ...prev,
    {
      title: draftTask.title,
      task_item: draftTask.items,
    },
  ]);

  // clear inputs
  setDraftTask({
    title: "",
    items: [],
    draftItem: "",
  });
};

 



  const [checklistName, setChecklistName] = useState("");
  const [description, setDescription] = useState("");
  const [checklistType, setChecklistType] = useState<string | null>(null);
 


  const closeAndReset = () => {
    setSearchRoleOpen(false);

    setRoleVal("");
    setRoleOptions([]);
    setSearchResults(null);
    setChecklistItem("");
    setSelectedUsage(null);
  
  setDraftTask({
    title: "",
    items: [],
    draftItem: "",
  });
  setSavedTasks([]);

  };

  useEffect(() => {
    if (searchRoleOpen) {
      setRoleVal("");
      setRoleOptions([]);
      setSearchResults(null);
      setChecklistItem("");
      setSelectedUsage(null);
     
     setDraftTask({
       title: "",
       items: [],
       draftItem: "",
     });
     setSavedTasks([]);

    }
  }, [searchRoleOpen]);

  const fetchRoles = useCallback(async (searching: string) => {
    if (!searching) {
      setRoleOptions([]);
      return;
    }
    setRolesLoading(true);
    try {
      const roles = await apiService.get<{ results: RoleItem[] }>(
        "/api/crews/roles/",
        { search: searching, page_size: 10 }
      );
      setRoleOptions(roles.data.results || []);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles(debouncedRoleSearch);
  }, [debouncedRoleSearch, fetchRoles]);

 

  const handleSaveChecklist = () => {
    onAddChecklist({
      role: searchResults,
      checklistItem: checklistName,
      usage: checklistType,
      description,
      tasks: savedTasks
    });

    closeAndReset();
  };

  

 

  


 


 

  const getUsageChipProps = (usage: string) => {
    switch (usage.toLowerCase()) {
      case "pre shoot":
        return { label: "Pre Shoot", bgcolor: "#F79E1B40", color: "#F79E1B" };
      case "post shoot":
        return { label: "Post Shoot", bgcolor: "#4BA66340", color: "#4BA663" };
      case "post production":
        return {
          label: "Post Production",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "final cut":
        return { label: "Final Cut", bgcolor: "#80008040", color: "#800080" };
      default:
        return { label: "Unknown", bgcolor: "#BDBDBD40", color: "#000" };
    }
  };

  const getInputTypeIcon = (inputType: string) => {
    switch (inputType.toLowerCase()) {
      case "text input":
        return <TextFields sx={{ fontSize: 18 }} />;

      case "file upload":
        return <FileUpload sx={{ fontSize: 18 }} />;

      case "checkbox":
        return <CheckBox sx={{ fontSize: 18 }} />;

      default:
        return <StreamsIcon sx={{ fontSize: 18 }} />; // fallback
    }
  };

  return (
    <Dialog
      open={searchRoleOpen}
      onClose={closeAndReset}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: "90%",
        },
      }}
    >
      <DialogTitle>
        Add Checklist / Task
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Checklist Name */}
        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Checklist Name
        </Typography>
        <CustomTextField
          placeholder="eg. Camera"
          value={checklistName}
          onChange={(e) => setChecklistName(e.target.value)}
          sx={{ mt: 1 }}
        />

        {/* Description */}

        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Description
        </Typography>
        <CustomTextField
          placeholder="Add context for crew member"
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Assign To Role
        </Typography>

        <Autocomplete
          multiple={false}
          options={roleOptions}
          getOptionLabel={(option) => option.name || "Unnamed Role"}
          value={searchResults}
          onChange={(_, newValue) => setSearchResults(newValue)}
          inputValue={roleVal}
          onInputChange={(_, newInput) => setRoleVal(newInput)}
          loading={rolesLoading}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              placeholder="Search roles..."
              variant="outlined"
              helperText="Type 3 letters to search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {rolesLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mt: 1 }}
        />

        <Autocomplete
          options={["Pre-stream", "On-stream", "Post-stream"]}
          value={checklistType}
          onChange={(_, v) => setChecklistType(v)}
          renderInput={(params) => (
            <CustomTextField {...params} label="Checklist Type" />
          )}
          sx={{ mt: 2 }}
        />

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={commitTask}
            disabled={!draftTask.title || draftTask.items.length === 0}
          >
            Add task items
          </Button>
          <Typography variant="subtitle2">Task Title</Typography>

          <CustomTextField
            placeholder="Tripod"
            value={draftTask.title}
            onChange={(e) =>
              setDraftTask((prev) => ({ ...prev, title: e.target.value }))
            }
          />

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Task Items
          </Typography>

          <CustomTextField
            placeholder="e.g. mount tripod"
            value={draftTask.draftItem}
            onChange={(e) =>
              setDraftTask((prev) => ({ ...prev, draftItem: e.target.value }))
            }
          />

          <Button size="small" sx={{ mt: 1 }} onClick={addDraftItem}>
            Add Item
          </Button>

          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={commitTask}
            disabled={!draftTask.title || draftTask.items.length === 0}
          >
            Add task items
          </Button>
        </Box>

        {/* Draft preview */}
        {draftTask.items.length > 0 && (
          <Box sx={{ mt: 2, pl: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Preview of {draftTask.title}
            </Typography>

            <Box sx={{ pl: 2, mt: 1 }}>
              {draftTask.items.map((item, index) => (
                <Typography key={index} variant="body2">
                  • {item.name}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {savedTasks.length > 0 && (
          <Box sx={{ mt: 3 }}>
            {savedTasks.map((task, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography fontWeight={600}>{task.title}</Typography>
                <Box sx={{ pl: 2 }}>
                  {task.task_item.map((item, i) => (
                    <Typography key={i} variant="body2">
                      • {item.name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <DialogActions>
          <Button onClick={closeAndReset}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveChecklist}>
            Add Checklist
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default SearchRoleDialog;
